import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  Box, Typography, TextField, Button, Avatar, IconButton, 
  List, ListItem, ListItemAvatar, ListItemText 
} from '@mui/material';
import { ArrowBackIos as ArrowBackIcon, Delete as DeleteIcon } from '@mui/icons-material';

export default function GroupProfile({ currentUserId }) {
  const { chatId } = useParams();
  const navigate = useNavigate();

  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Состояния для редактирования
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editAvatar, setEditAvatar] = useState(null);

  useEffect(() => {
    fetchGroupDetails();
  }, [chatId]);

  const fetchGroupDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`/api/groups/${chatId}`);
      if (!res.ok) throw new Error('Ошибка');
      
      const data = await res.json();
      setGroup(data);
      
      // Инициализируем данные для формы редактирования
      setEditName(data.title || data.name || '');
      setEditDescription(data.description || '');
      setEditAvatar(data.avatar || null);
    } catch (err) {
      console.error(err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Переключение в режим редактирования с сохранением старой аватарки
  const handleStartEditing = () => {
    setEditName(group?.title || group?.name || '');
    setEditDescription(group?.description || '');
    setEditAvatar(group?.avatar || null);
    setIsEditing(true);
  };

  const handleAvatarChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = () => setEditAvatar(reader.result);
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleSave = async () => {
    try {
      const avatarToSave = editAvatar || group.avatar;

      const res = await fetch(`/api/groups/${chatId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: editName,
          description: editDescription,
          avatar: avatarToSave,
          requesterId: currentUserId
        })
      });

      if (res.ok) {
        alert('Группа обновлена');
        setIsEditing(false);
        fetchGroupDetails(); // Обновляем данные
      } else {
        const errorText = await res.text();
        alert(`Ошибка: ${errorText}`);
      }
    } catch (err) {
      console.error(err);
      alert(`Ошибка: ${err.message}`);
    }
  };

  const handleKickMember = async (targetUserId, e) => {
    e.stopPropagation(); // Не переходим в профиль при клике на корзину
    if (!window.confirm('Вы действительно хотите его кикнуть?')) return;

    try {
      const res = await fetch(`/api/groups/${chatId}/kick`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          targetUserId: targetUserId,
          requesterId: currentUserId
        })
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setGroup(prev => ({
          ...prev,
          members: prev.members.filter(m => m.userId !== targetUserId)
        }));
      } else {
        alert(data.error || 'Ошибка удаления');
      }
    } catch (err) {
      console.error(err);
      alert('Ошибка');
    }
  };

  // Переход в профили
  const handleMemberClick = (member) => {
    const isBot = member.isBot === 1 || member.isBot === true || String(member.userId) === String(group?.botId);
    
    if (isBot) {
      // Бот -> профиль бота
      const botIdToNavigate = member.botId || member.userId || group?.botId;
      navigate(`/bot/${botIdToNavigate}`);
    } else if (String(member.userId) === String(currentUserId)) {
      // Свой профиль
      navigate('/profile');
    } else {
      // Чужой профиль
      navigate(`/profile/${member.userId}`);
    }
  };

  // Сортировка участников: 1. Бот, 2. Создатель, 3. Остальные
  const sortedMembers = useMemo(() => {
    if (!group?.members) return [];
    
    return [...group.members].sort((a, b) => {
      const aIsBot = a.isBot === 1 || a.isBot === true || String(a.userId) === String(group.botId);
      const bIsBot = b.isBot === 1 || b.isBot === true || String(b.userId) === String(group.botId);
      
      if (aIsBot && !bIsBot) return -1;
      if (!aIsBot && bIsBot) return 1;

      const aIsCreator = String(a.userId) === String(group.createdBy);
      const bIsCreator = String(b.userId) === String(group.createdBy);

      if (aIsCreator && !bIsCreator) return -1;
      if (!aIsCreator && bIsCreator) return 1;

      return 0;
    });
  }, [group]);

  if (loading) return <Typography sx={{ textAlign: 'center', mt: 10, color: 'white' }}>Загрузка группы...</Typography>;
  
  if (error || !group) {
    return (
      <Box sx={{ p: 2, textAlign: 'center', color: 'white' }}>
        <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', position: 'absolute', left: 10, top: 10 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography sx={{ mt: 10 }}>Ошибка: {error || 'Группа не найдена'}</Typography>
      </Box>
    );
  }

  const isCreator = String(group.createdBy) === String(currentUserId);

  return (
    <Box sx={{ p: 2, overflowY: 'auto', pb: '120px', color: 'white', position: 'relative' }}>
      
      {/* Кнопка "Назад" */}
      <IconButton onClick={() => navigate(-1)} sx={{ color: 'white', position: 'absolute', left: 10, top: 10 }}>
        <ArrowBackIcon />
      </IconButton>

      <Typography variant="h5" sx={{ mb: 3, fontWeight: 'bold', textAlign: 'center' }}>
        Профиль группы
      </Typography>

      {/* Аватарка */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        {isEditing ? (
          <label htmlFor="group-avatar-upload">
            <Avatar 
              src={editAvatar || group.avatar} 
              sx={{ width: 120, height: 120, cursor: 'pointer', border: '2px solid #444' }} 
            />
            <input 
              id="group-avatar-upload" 
              type="file" 
              accept="image/*" 
              onChange={handleAvatarChange} 
              style={{ display: 'none' }} 
            />
          </label>
        ) : (
          <Avatar 
            src={group.avatar} 
            sx={{ width: 120, height: 120, border: '2px solid #444' }} 
          />
        )}
      </Box>

      {/* Информационный блок / Редактирование */}
      {isEditing ? (
        <Box sx={{ mt: 2 }}>
          <TextField 
            fullWidth 
            label="Название группы" 
            value={editName} 
            onChange={e => setEditName(e.target.value)} 
            margin="normal"
            InputLabelProps={{ style: { color: '#aaa', fontStyle: 'italic' } }}
            sx={{ input: { color: 'white' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#444' } } }}
          />
          <TextField 
            fullWidth 
            label="Описание группы" 
            value={editDescription} 
            onChange={e => setEditDescription(e.target.value)} 
            multiline 
            rows={3} 
            margin="normal"
            InputLabelProps={{ style: { color: '#aaa', fontStyle: 'italic' } }}
            sx={{ textarea: { color: 'white' }, '& .MuiOutlinedInput-root': { '& fieldset': { borderColor: '#444' } } }}
          />
        </Box>
      ) : (
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            {group.title || group.name || 'Без названия'}
          </Typography>
          
          {group.code && (
            <Box sx={{ background: '#2a2a2a', padding: '6px 12px', borderRadius: 2, display: 'inline-block', mb: 2 }}>
              Код группы: <Typography component="span" sx={{ fontWeight: 'bold' }}>{group.code}</Typography>
            </Box>
          )}
          
          {/* Отображение описания группы */}
          <Box sx={{ mt: 1, px: 2 }}>
            <Typography variant="body1" sx={{ color: group.description ? '#ddd' : '#777', fontStyle: group.description ? 'normal' : 'italic', wordBreak: 'break-word' }}>
              {group.description || 'Описание отсутствует'}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Список участников */}
      <Typography variant="h6" sx={{ mt: 3, mb: 1, fontWeight: 'bold', textAlign: 'center' }}>
        Участники ({sortedMembers.length})
      </Typography>
      
      <List sx={{ width: '100%', bgcolor: 'transparent', p: 0 }}>
        {sortedMembers.map((member) => {
          const isMemberCreator = String(member.userId) === String(group.createdBy);
          const isBot = member.isBot === 1 || member.isBot === true || String(member.userId) === String(group.botId);
          
          // Владелец не может кикнуть сам себя и бота
          const canKick = isCreator && !isMemberCreator && !isBot;

          return (
            <ListItem 
              key={member.userId} 
              sx={{ 
                borderBottom: '1px solid #333', 
                cursor: 'pointer',
                borderRadius: '8px',
                my: 0.5,
                transition: 'background-color 0.2s',
                '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.08)' } 
              }}
              onClick={() => handleMemberClick(member)}
              secondaryAction={
                canKick ? (
                  <IconButton 
                    edge="end" 
                    title="Кикнуть"
                    onClick={(e) => handleKickMember(member.userId, e)}
                    sx={{ color: '#ff4d4f' }}
                  >
                    <DeleteIcon />
                  </IconButton>
                ) : null
              }
            >
              <ListItemAvatar>
                <Avatar src={member.avatar || ''} alt={member.name} />
              </ListItemAvatar>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ color: 'white', fontWeight: '500' }}>
                      {member.name || `Пользователь #${member.userId}`}
                    </Typography>
                    {isBot && <Typography variant="caption" sx={{ color: '#ab47bc', fontWeight: 'bold' }}>(Бот)</Typography>}
                    {!isBot && isMemberCreator && <Typography variant="caption" sx={{ color: '#ffd700', fontWeight: 'bold' }}>(Создатель)</Typography>}
                  </Box>
                }
              />
            </ListItem>
          );
        })}
      </List>

      {/* Кнопки редактирования (для создателя) */}
      {isCreator && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 4 }}>
          <Button 
            variant="contained" 
            fullWidth 
            onClick={isEditing ? handleSave : handleStartEditing} 
            sx={{ 
              borderRadius: '20px', 
              backgroundColor: isEditing ? 'green' : 'blue',
              color: 'white',
              '&:hover': { backgroundColor: isEditing ? 'darkgreen' : 'darkblue' }
            }}
          >
            {isEditing ? 'Сохранить изменения' : 'Изменить профиль группы'}
          </Button>

          {isEditing && (
            <Button 
              variant="outlined" 
              fullWidth 
              onClick={() => {
                setIsEditing(false);
                setEditName(group.title || group.name || '');
                setEditDescription(group.description || '');
                setEditAvatar(group.avatar || null);
              }}
              sx={{ borderRadius: '20px', color: '#aaa', borderColor: '#444' }}
            >
              Отмена
            </Button>
          )}
        </Box>
      )}

    </Box>
  );
}